import { useRef, forwardRef, useImperativeHandle } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './button';
import { Eraser, RotateCcw } from 'lucide-react';

const SignaturePad = forwardRef(({ 
  width = 400, 
  height = 150, 
  penColor = '#000000',
  backgroundColor = '#ffffff',
  className = '',
  onEnd,
  label = 'Signature',
  showButtons = true
}, ref) => {
  const sigCanvas = useRef(null);

  useImperativeHandle(ref, () => ({
    clear: () => {
      sigCanvas.current?.clear();
    },
    isEmpty: () => {
      return sigCanvas.current?.isEmpty() ?? true;
    },
    getDataURL: (type = 'image/png') => {
      if (sigCanvas.current?.isEmpty()) return null;
      // Use getCanvas directly instead of getTrimmedCanvas to avoid trim-canvas dependency issues
      const canvas = sigCanvas.current?.getCanvas();
      if (!canvas) return null;
      return canvas.toDataURL(type);
    },
    getCanvas: () => {
      return sigCanvas.current?.getCanvas();
    },
    toData: () => {
      return sigCanvas.current?.toData();
    },
    fromDataURL: (dataURL) => {
      sigCanvas.current?.fromDataURL(dataURL);
    }
  }));

  const handleClear = () => {
    sigCanvas.current?.clear();
  };

  const handleUndo = () => {
    const data = sigCanvas.current?.toData();
    if (data && data.length > 0) {
      data.pop();
      sigCanvas.current?.fromData(data);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-foreground">{label}</label>
      )}
      <div className="relative">
        <div 
          className="border-2 border-dashed border-border rounded-lg overflow-hidden bg-white"
          style={{ width, height }}
        >
          <SignatureCanvas
            ref={sigCanvas}
            penColor={penColor}
            backgroundColor={backgroundColor}
            canvasProps={{
              width,
              height,
              className: 'signature-canvas',
              style: { width: '100%', height: '100%' }
            }}
            onEnd={onEnd}
          />
          
          {/* Guide line */}
          <div 
            className="absolute bottom-8 left-4 right-4 border-b border-gray-300 pointer-events-none"
            style={{ borderStyle: 'dashed' }}
          />
          <span className="absolute bottom-2 left-4 text-xs text-gray-400 pointer-events-none">
            Signez ici
          </span>
        </div>
        
        {showButtons && (
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 bg-white/80 hover:bg-white"
              onClick={handleUndo}
              title="Annuler le dernier trait"
            >
              <RotateCcw className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 bg-white/80 hover:bg-white"
              onClick={handleClear}
              title="Effacer"
            >
              <Eraser className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export { SignaturePad };
